import React from 'react';

import {Separator} from 'src/components/separator';
import {CommentsController} from 'src/modules/comments/comments.controller';
import {
    ChangeRequestMetadata,
} from 'src/modules/event-create/header-block/change-request-metadata/change-request-metadata';
import {Deprecated} from 'src/modules/event-create/header-block/deprecated/deprecated';
import {EventCode} from 'src/modules/event-create/header-block/event-code/event-code';
import {EventDecoration} from 'src/modules/event-create/header-block/event-decoration/event-decoration';
import {EventDescription} from 'src/modules/event-create/header-block/event-description/event-description';
import {EventSource} from 'src/modules/event-create/header-block/event-source/event-source';
import {EventTrigger} from 'src/modules/event-create/header-block/event-trigger/event-trigger';
import {
    EventTriggerAttachmentController,
} from 'src/modules/event-create/header-block/event-trigger-attachment/event-trigger-attachment.controller';
import {EventType} from 'src/modules/event-create/header-block/event-type/event-type';
import {Families} from 'src/modules/event-create/header-block/families/families';
import {Reported} from 'src/modules/event-create/header-block/reported/reported';
import {getChangeRequestInfo} from 'src/modules/event-create/services';
import {HeaderBlock} from 'src/modules/event-create/typedefs';
import {getEventsInfo} from 'src/modules/events/services';
import {ChangeRequestContextualLink} from 'src/openapi/models/ChangeRequestContextualLink';
import {DecorationType} from 'src/openapi/models/DecorationType';
import {EventChangeRequest} from 'src/openapi/models/EventChangeRequest';
import {useAppSelector} from 'src/store';

import {Group, Item} from '@pb/kit-component-group';

import {EventCreationMode} from '../constants';

type Props = {
    typesList: string[],
    sources: string[],
    headerValues: HeaderBlock,
    mode: EventCreationMode,
    validationHighLight: boolean,
    changeRequestId: number | null,
    eventChangeRequest: EventChangeRequest,
    defaultDueDate: Date | undefined,
    onChangeContextualLinks: (value: ChangeRequestContextualLink[]) => void,
    onChangeDecorationType: (value: DecorationType | null) => void,
    onChangeField: (value: string, name?: string) => void,
    onChangeFamilies: (value: string[]) => void,
    onChangeCheckboxField: (data: {event: React.SyntheticEvent<HTMLInputElement>, name: string}) => void,
    onInputBlur: (event: React.FocusEvent<HTMLInputElement>, name?: string) => void,
    onTriggerBlur: (event: React.FocusEvent<HTMLTextAreaElement>) => void,
    onChangeDueDate: (date?: Date) => void,
};

export const HeaderBlockView = (props: Props) => {
    const {
        typesList,
        sources,
        headerValues,
        mode,
        validationHighLight,
        eventChangeRequest,
        changeRequestId,
        defaultDueDate,
        onChangeContextualLinks,
        onInputBlur,
        onTriggerBlur,
        onChangeCheckboxField,
        onChangeField,
        onChangeFamilies,
        onChangeDueDate,
        onChangeDecorationType,
    } = props;

    const changeRequestInfo = useAppSelector(getChangeRequestInfo)?.[changeRequestId ?? 0];
    const eventsInfo = useAppSelector(getEventsInfo);

    return (
        <Group justify="space-between" gap="xls" alignItems="start">
            <Item style={{width: '50%'}}>
                <Group direction="v" gap="xl">
                    <Item style={{width: '100%'}}>
                        <Group alignItems="start">
                            <Item style={{width: '100%'}}>
                                <Group direction="v" gap="sm">
                                    <EventType
                                        eventsInfo={eventsInfo}
                                        mode={mode}
                                        changeRequestId={changeRequestId}
                                        validationHighLight={validationHighLight}
                                        headerValues={headerValues}
                                        typesList={typesList}
                                        onChangeField={onChangeField}
                                    />
                                    <EventSource
                                        eventsInfo={eventsInfo}
                                        mode={mode}
                                        changeRequestId={changeRequestId}
                                        validationHighLight={validationHighLight}
                                        headerValues={headerValues}
                                        sourcesList={sources}
                                        onChangeField={onChangeField}
                                    />
                                    <EventTrigger
                                        headerValues={headerValues}
                                        validationHighLight={validationHighLight}
                                        changeRequestId={changeRequestId}
                                        onChangeField={onChangeField}
                                        onTriggerBlur={onTriggerBlur}
                                        eventsInfo={eventsInfo}
                                    />
                                    <EventDescription
                                        headerValues={headerValues}
                                        mode={mode}
                                        eventChangeRequest={eventChangeRequest}
                                        changeRequestId={changeRequestId}
                                        validationHighLight={validationHighLight}
                                        onInputBlur={onInputBlur}
                                        onChangeField={onChangeField}
                                    />
                                    <EventCode
                                        headerValues={headerValues}
                                        mode={mode}
                                        eventChangeRequest={eventChangeRequest}
                                        changeRequestId={changeRequestId}
                                        validationHighLight={validationHighLight}
                                        onChangeField={onChangeField}
                                    />
                                    <Families
                                        headerValues={headerValues}
                                        changeRequestId={changeRequestId}
                                        onChangeFamilies={onChangeFamilies}
                                    />
                                    <Item style={{width: '100%', minHeight: '4.5rem'}}>
                                        <Group>
                                            <Group gap="l">
                                                <Reported
                                                    headerValues={headerValues}
                                                    changeRequestId={changeRequestId}
                                                    onChangeCheckboxField={onChangeCheckboxField}
                                                />
                                                <Deprecated
                                                    mode={mode}
                                                    headerValues={headerValues}
                                                    changeRequestId={changeRequestId}
                                                    onChangeCheckboxField={onChangeCheckboxField}
                                                />
                                                <Item style={{width: '100%'}}>
                                                    <EventDecoration
                                                        headerValues={headerValues}
                                                        changeRequestId={changeRequestId}
                                                        onChangeCheckboxField={onChangeCheckboxField}
                                                        onChangeDecorationType={onChangeDecorationType}
                                                    />
                                                </Item>
                                            </Group>
                                        </Group>
                                    </Item>
                                    <ChangeRequestMetadata
                                        changeRequestInfo={changeRequestInfo}
                                        validationHighLight={validationHighLight}
                                        onChangeContextualLinks={onChangeContextualLinks}
                                        defaultDueDate={defaultDueDate}
                                        onChangeDueDate={onChangeDueDate}
                                    />
                                </Group>
                            </Item>
                        </Group>
                    </Item>
                </Group>
            </Item>
            <Group direction="v" style={{width: '45%'}}>
                <EventTriggerAttachmentController
                    mode={mode}
                    eventChangeRequest={eventChangeRequest}
                    validationHighLight={validationHighLight}
                />
                <Separator/>
                <CommentsController changeRequest={changeRequestInfo} changeRequestId={changeRequestId}/>
            </Group>
        </Group>
    );
};
